import { useContext, useEffect } from 'react';

import { Money, formatMoney, toMoney } from '@betterment/js-runtime';
import { Box, Button, FormikField as Field, Input, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { LegacyTransferType } from '../../../../../graphql/schema';
import {
  calculateCashflows,
  convertToMoneyWithDefault,
  hasMaskedFormFieldChanged,
} from '../../../../shared/helpers/utils';
import { ForecasterPurposeInputsContext, ProjectionInputs } from '../../../context/ForecasterPurposeInputsContext';
import { useEmergencyFundProjection } from '../../../context/ForecasterPurposeProjectionContext';
import { EmergencyFundSettingsPurpose } from '../../../data';
import { handleSetupOneTimeDeposit, handleSetupRecurringDeposit } from '../utils';

type Props = {
  purpose: EmergencyFundSettingsPurpose;
};

const schema = Yup.object().shape({
  adHocInflow: Yup.string().optional(),
  monthlyRecurringDeposits: Yup.string().optional(),
});

type FormikFormProps = {
  recommendedMonthlyDeposits?: Money | null;
  otherCashflows?: Money | null;
  updatedFormInputs?: ProjectionInputs['emergencyFund'];
  legacySubAccountId: string;
  automaticDepositConfigId?: number | null;
};

const FormikForm = ({
  recommendedMonthlyDeposits,
  otherCashflows,
  updatedFormInputs,
  legacySubAccountId,
  automaticDepositConfigId,
}: FormikFormProps) => {
  const { handleSubmit, initialValues, setFieldValue, submitForm, validateForm, values } = useFormikContext<{
    adHocInflow?: string;
    monthlyRecurringDeposits?: string;
  }>();

  useEffect(() => {
    void validateForm();
    void submitForm();
  }, []);

  const handleFormChange = async () => {
    await validateForm();
    handleSubmit();
  };

  const shouldShowRecommendedMonthlyDeposits =
    !recommendedMonthlyDeposits ||
    recommendedMonthlyDeposits?.cents !== updatedFormInputs?.monthlyRecurringDeposits?.cents;

  const handleRecommendedMonthlyDepositsClick = async () => {
    if (recommendedMonthlyDeposits) {
      await setFieldValue('monthlyRecurringDeposits', recommendedMonthlyDeposits.dollars.toString(), true);
      await handleFormChange();
    }
  };

  return (
    <Form>
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'space.200',
        }}
      >
        <Field name="adHocInflow">
          <Field.Label>One-time contribution</Field.Label>
          <Field.Control>
            <Input
              mask="number"
              leadingSymbol="$"
              placeholder="0"
              aria-label="One time contribution amount"
              onChange={handleFormChange}
            />
          </Field.Control>
          <Field.ErrorMessage />
        </Field>
        {values.adHocInflow && hasMaskedFormFieldChanged(values.adHocInflow, initialValues.adHocInflow) && (
          <SambaAutoLink to={handleSetupOneTimeDeposit(legacySubAccountId, values.adHocInflow)}>
            Continue to deposit
          </SambaAutoLink>
        )}
        <Box>
          <Field name="monthlyRecurringDeposits">
            <Field.Label>Monthly contributions</Field.Label>
            <Field.Control>
              <Input
                mask="number"
                leadingSymbol="$"
                placeholder="0"
                aria-label="Monthly contributions amount"
                onChange={handleFormChange}
              />
            </Field.Control>
            <Field.ErrorMessage />
          </Field>
          {shouldShowRecommendedMonthlyDeposits && recommendedMonthlyDeposits && (
            <Text variant="text50" css={{ mt: 'space.50' }}>
              Recommended:{' '}
              <Button variant="textPrimary" onClick={handleRecommendedMonthlyDepositsClick}>
                {`${formatMoney(recommendedMonthlyDeposits)}/month`}
              </Button>
            </Text>
          )}
        </Box>
        {values.monthlyRecurringDeposits &&
          hasMaskedFormFieldChanged(values.monthlyRecurringDeposits, initialValues.monthlyRecurringDeposits) && (
            <SambaAutoLink
              to={handleSetupRecurringDeposit(
                legacySubAccountId,
                values.monthlyRecurringDeposits,
                automaticDepositConfigId
              )}
            >
              Continue to deposit
            </SambaAutoLink>
          )}
        {otherCashflows && (
          <Text variant="text50">Other planned transactions: {`${formatMoney(otherCashflows)}/month`}</Text>
        )}
      </Box>
    </Form>
  );
};

export const EmergencyFundForm: React.FC<Props> = ({ purpose }) => {
  const inputsContext = useContext(ForecasterPurposeInputsContext);
  const { purpose: projectionPurpose } = useEmergencyFundProjection();

  if (!inputsContext) return null;

  const {
    projectionQueryInputs: { emergencyFund: updatedFormInputs },
    update: { emergencyFundProjectionInput: updateInputs },
  } = inputsContext;

  const { envelope } = purpose;
  const { legalSubAccounts, monthlyNetRecurringCashflows, recurringTransactionConfigs } = envelope;

  const { initialEditableAmount, editableConfigs, otherCashflows } = calculateCashflows({
    monthlyNetRecurringCashflows: monthlyNetRecurringCashflows || toMoney(0),
    legacyTransferType: LegacyTransferType.Deposit,
    configs: recurringTransactionConfigs,
  });

  return (
    <Formik
      onSubmit={values => {
        updateInputs({
          adHocInflow: convertToMoneyWithDefault(values.adHocInflow),
          monthlyRecurringDeposits: convertToMoneyWithDefault(values.monthlyRecurringDeposits),
        });
      }}
      validationSchema={schema}
      initialValues={{
        adHocInflow: '',
        monthlyRecurringDeposits: initialEditableAmount ? initialEditableAmount.dollars.toString() : '',
      }}
    >
      <FormikForm
        recommendedMonthlyDeposits={projectionPurpose?.emergencyFundProjection?.recommendedMonthlyDeposits}
        otherCashflows={otherCashflows}
        updatedFormInputs={updatedFormInputs}
        automaticDepositConfigId={editableConfigs[0]?.legacyId}
        legacySubAccountId={legalSubAccounts[0].legacySubAccountId.toString()}
      />
    </Formik>
  );
};
